/**
 * Shared Type Definitions for PDF to LaTeX Processor
 * Multi-role AI Collaboration System
 */

// ==================== AI Provider Configuration ====================

export type AIProvider = 'deepseek' | 'openai' | 'anthropic' | 'qwen' | 'doubao' | 'custom';

export interface BaseAIConfig {
  apiProvider: AIProvider;
  model: string;
  apiKey: string;
  temperature: number;
  maxTokens?: number;
  baseURL?: string; // For custom providers
}

// ==================== Four AI Roles Configuration ====================

export interface InterpreterConfig extends BaseAIConfig {
  role: 'interpreter';
}

export interface LogicCheckerConfig extends BaseAIConfig {
  role: 'logic_checker';
}

export interface SolutionGeneratorConfig extends BaseAIConfig {
  role: 'solution_generator';
  detailLevel: 'basic' | 'detailed' | 'comprehensive';
  stepByStep: boolean;
  includeVerification: boolean;
}

export interface ResultValidatorConfig extends BaseAIConfig {
  role: 'result_validator';
}

export interface RoleConfigurations {
  interpreter: InterpreterConfig;
  logicChecker: LogicCheckerConfig;
  solutionGenerator: SolutionGeneratorConfig;
  resultValidator: ResultValidatorConfig;
}

// ==================== Document Processing ====================

export interface DocumentMetadata {
  fileName: string;
  fileSize: number;
  pageCount: number;
  uploadedAt: Date;
  processedAt?: Date;
}

export interface Problem {
  id: string;
  content: string;
  pageNumber: number;
  type: 'multiple_choice' | 'short_answer' | 'proof' | 'calculation' | 'other';
  difficulty?: 'easy' | 'medium' | 'hard';
  difficultyScore?: number; // 0-5 rating
  subject?: string;
  originalText: string;
}

export interface SolutionStep {
  stepNumber: number;
  description: string;
  formula?: string;
  explanation: string;
}

export interface Solution {
  problemId: string;
  steps: SolutionStep[];
  finalAnswer: string;
  explanation: string;
  generatedAt: Date;
}

// ==================== AI Role Results ====================

export interface InterpretationResult {
  problems: Problem[];
  metadata: DocumentMetadata;
  confidence: number;
  processingTime: number;
}

export interface LogicCheckResult {
  problemId: string;
  solutionId: string;
  isCoherent: boolean;
  inconsistencies: string[];
  suggestions: string[];
  stepBreakdown: SolutionStep[];
  confidence: number;
}

export interface GeneratedSolution extends Solution {
  alternativeMethods?: string[];
  complexity: 'low' | 'medium' | 'high';
}

export interface ValidationResult {
  solutionId: string;
  isValid: boolean;
  errors: string[];
  corrections: string[];
  overallScore: number;
  detailedFeedback: string;
}

// ==================== Processing Pipeline ====================

export enum ProcessingStep {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  EXTRACTING_TEXT = 'extracting_text',
  INTERPRETING = 'interpreting',
  GENERATING_SOLUTIONS = 'generating_solutions',
  LOGIC_CHECKING = 'logic_checking',
  VALIDATING = 'validating',
  GENERATING_LATEX = 'generating_latex',
  COMPLETED = 'completed',
  ERROR = 'error'
}

export interface ProcessingState {
  processId: string;
  currentStep: ProcessingStep;
  progress: number; // 0-100
  statusMessages: string[];
  currentAnimation: string;
  lastUpdated: Date;
  error?: string;
}

export interface ProcessingResult {
  processId: string;
  interpretation: InterpretationResult;
  solutions: GeneratedSolution[];
  logicChecks: LogicCheckResult[];
  validations: ValidationResult[];
  latexDocument: string;
  completedAt: Date;
}

// ==================== LaTeX Generation ====================

export interface LaTeXOptions {
  documentClass: string;
  packages: string[];
  includeAnswers: boolean;
  includeSolutions: boolean;
  formatting: {
    fontSize: string;
    margin: string;
    lineSpacing: string;
  };
}

export interface LaTeXDocument {
  content: string;
  problems: Problem[];
  solutions: Solution[];
  metadata: DocumentMetadata;
}

// ==================== API Request/Response ====================

export interface ProcessPDFRequest {
  file: File;
  roleConfigurations: RoleConfigurations;
  latexOptions?: LaTeXOptions;
}

export interface ProcessPDFResponse {
  processId: string;
  status: 'started' | 'processing' | 'completed' | 'failed';
  message: string;
}

export interface StatusResponse {
  processId: string;
  state: ProcessingState;
  result?: ProcessingResult;
}

// ==================== Storage ====================

export interface KVProcessingState {
  processId: string;
  state: ProcessingState;
  result?: ProcessingResult;
  createdAt: number;
  expiresAt: number;
}

export interface R2FileMetadata {
  processId: string;
  originalFileName: string;
  uploadedAt: number;
  size: number;
  contentType: string;
}

// ==================== Animation Messages ====================

export interface AnimationMessage {
  step: ProcessingStep;
  messages: string[];
}

export const ANIMATION_MESSAGES: Record<ProcessingStep, string[]> = {
  [ProcessingStep.IDLE]: ['准备就绪...'],
  [ProcessingStep.UPLOADING]: [
    '正在上传文件...',
    '验证文件格式...',
    '准备处理...'
  ],
  [ProcessingStep.EXTRACTING_TEXT]: [
    '正在提取PDF文本...',
    '识别文档结构...',
    '解析页面内容...'
  ],
  [ProcessingStep.INTERPRETING]: [
    '正在分析文档结构...',
    '识别数学公式和符号...',
    '分解题目内容...',
    '提取关键信息...',
    '分类问题类型...'
  ],
  [ProcessingStep.GENERATING_SOLUTIONS]: [
    '生成解题思路...',
    '分解解题步骤...',
    '验证方法可行性...',
    '优化解决方案...',
    '添加详细说明...'
  ],
  [ProcessingStep.LOGIC_CHECKING]: [
    '检查逻辑连贯性...',
    '验证推理步骤...',
    '识别潜在错误...',
    '提供改进建议...'
  ],
  [ProcessingStep.VALIDATING]: [
    '验证最终答案...',
    '检查计算准确性...',
    '评估解决方案质量...',
    '生成反馈报告...'
  ],
  [ProcessingStep.GENERATING_LATEX]: [
    '生成LaTeX文档...',
    '格式化数学公式...',
    '排版解决方案...',
    '优化文档结构...'
  ],
  [ProcessingStep.COMPLETED]: ['处理完成！'],
  [ProcessingStep.ERROR]: ['处理出错，请重试...']
};

// ==================== Error Handling ====================

export class ProcessingError extends Error {
  constructor(
    message: string,
    public step: ProcessingStep,
    public details?: any
  ) {
    super(message);
    this.name = 'ProcessingError';
  }
}

export interface RetryConfig {
  maxRetries: number;
  retryDelay: number; // milliseconds
  backoffMultiplier: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000,
  backoffMultiplier: 2
};
